import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useNavigate, useParams, useMatch } from 'react-router-dom';
import { viewClient } from '../api/viewClientApiService';
import { listSubscriptionApiService } from '@/epics/subscriptionManagment/api/listSubscriptionApiService';
import type { SubscriptionPlan } from '@/epics/subscriptionManagment/api/viewSubscriptionPlanApiService';
import type { AddClientDetails, MediaResource } from '../types/addClientType';
import { CLIENT_ERROR_MESSAGES } from '../errors/errorMessages';
import {
  editActiveClient,
  type ClientPatchPayload,
} from '../api/editActiveClientApiService';
import { deleteMediaResource } from '../api/deleteMediaResourceApiService';
import { getPresignedUrlsBatch } from '@/epics/technicianManagement/api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '@shared/utils/uploadFileToS3';
import { updateClientStatusApiService } from '../api/updateClientStatusApiService';
import { CLIENT_LABELS } from '../constants/labels';
import {
  validateName,
  validatePhone,
  validateAddress,
  validateCity,
  validateState,
  validateNip,
  validateZip,
  validateBillingRegon,
  validateBillingCompanyName,
  validateBillingNip,
  validateBillingKrs,
  validateInvoiceAddressLine1,
  validateInvoiceZipCode,
  validateInvoiceCity,
} from '../utils/validation';
import { useAddClientStore } from '../store/addClientStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ClientErrors = Omit<AddClientDetails, 'profileFile' | 'contractFiles'> & {
  profileFile?: string;
  contractFiles?: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const initialFormData: AddClientDetails = {
  name: '',
  email: '',
  phone: '',
  countryCode: '+48',
  currency: 'PLN',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
  nip: '',
  subscriptionPlan: '',
  paymentStatus: '',
  profileFile: undefined,
  contractFiles: undefined,
  // Invoice details (Dane do faktury)
  billing_company_name: '',
  billing_nip: '',
  billing_regon: '',
  billing_krs: '',
  invoice_address_line_1: '',
  invoice_address_line_2: '',
  invoice_zip_code: '',
  invoice_city: '',
  invoice_province: '',
};

const initialErrors: ClientErrors = {
  name: '',
  email: '',
  phone: '',
  countryCode: '',
  currency: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  nip: '',
  subscriptionPlan: '',
  paymentStatus: '',
  billing_company_name: '',
  billing_nip: '',
  billing_regon: '',
  billing_krs: '',
  invoice_address_line_1: '',
  invoice_address_line_2: '',
  invoice_zip_code: '',
  invoice_city: '',
  invoice_province: '',
};

type StringFormKey = keyof Omit<
  AddClientDetails,
  'profileFile' | 'contractFiles'
>;

// Email is excluded — it is non-editable in edit mode and never sent in PATCH.
const FIELD_MAPPINGS: Array<{
  from: StringFormKey;
  to: keyof ClientPatchPayload;
  normalize: (v: string) => string;
}> = [
  { from: 'name', to: 'name', normalize: (v) => v.trim() },
  { from: 'phone', to: 'phoneNumber', normalize: (v) => v.trim() },
  { from: 'countryCode', to: 'countryCode', normalize: (v) => v },
  { from: 'nip', to: 'nip', normalize: (v) => v },
  { from: 'currency', to: 'currency', normalize: (v) => v },
  { from: 'paymentStatus', to: 'paymentStatus', normalize: (v) => v },
  { from: 'subscriptionPlan', to: 'subscriptionPlanId', normalize: (v) => v },
  { from: 'address1', to: 'addressLine1', normalize: (v) => v.trim() },
  { from: 'address2', to: 'addressLine2', normalize: (v) => v.trim() },
  { from: 'city', to: 'city', normalize: (v) => v.trim() },
  { from: 'state', to: 'state', normalize: (v) => v.trim() },
  { from: 'zip', to: 'zipCode', normalize: (v) => v.trim() },
  { from: 'country', to: 'country', normalize: (v) => v },
  // Invoice details (Dane do faktury)
  {
    from: 'billing_company_name',
    to: 'billing_company_name',
    normalize: (v) => v.trim(),
  },
  {
    from: 'billing_nip',
    to: 'billing_nip',
    normalize: (v) => v.replace(/-/g, '').trim(),
  },
  {
    from: 'billing_regon',
    to: 'billing_regon',
    normalize: (v) => v.replace(/\D/g, '').trim(),
  },
  {
    from: 'billing_krs',
    to: 'billing_krs',
    normalize: (v) => v.replace(/\D/g, '').trim(),
  },
  {
    from: 'invoice_address_line_1',
    to: 'invoice_address_line_1',
    normalize: (v) => v.trim(),
  },
  {
    from: 'invoice_address_line_2',
    to: 'invoice_address_line_2',
    normalize: (v) => v.trim(),
  },
  {
    from: 'invoice_zip_code',
    to: 'invoice_zip_code',
    normalize: (v) => v.trim(),
  },
  { from: 'invoice_city', to: 'invoice_city', normalize: (v) => v.trim() },
  {
    from: 'invoice_province',
    to: 'invoice_province',
    normalize: (v) => v.trim(),
  },
];

const buildTextDiff = (
  current: AddClientDetails,
  committed: AddClientDetails
): ClientPatchPayload =>
  FIELD_MAPPINGS.reduce<ClientPatchPayload>((diff, { from, to, normalize }) => {
    const currentVal = normalize(current[from] as string);
    const committedVal = normalize(committed[from] as string);
    return currentVal !== committedVal ? { ...diff, [to]: currentVal } : diff;
  }, {});

/**
 * Polish NIP display format: `XXX-XXX-XX-XX` (10 digits).
 * Matches the keystroke formatter in {@link useAddClient.handleNipChange}
 * so server values without hyphens render the same as freshly-typed ones.
 */
const formatNip = (raw: string | null | undefined): string => {
  const digits = (raw ?? '').replace(/\D/g, '').slice(0, 10);
  if (digits.length > 8) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  if (digits.length > 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return digits;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useEditClient = () => {
  const navigate = useNavigate();
  const { clientId = '' } = useParams<{ clientId?: string }>();
  const matchStep2 = useMatch('/Klienci/Edycja-klienta/:clientId/krok-2');
  const step: 1 | 2 = matchStep2 ? 2 : 1;

  const { saveDraft, getDraft, clearDraft } = useAddClientStore();

  // ── Refs (must be declared before state initializers) ─────────────────────
  const committedFormData = useRef<AddClientDetails | null>(null);
  // Tracks the S3 path of the profile currently on the server.
  // Updated after each successful PATCH so profile deletion always targets
  // the correct object.
  const originalProfilePath = useRef<string | null>(null);

  // ── Initial data load ────────────────────────────────────────────────────
  const [isLoadingClientData, setIsLoadingClientData] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clientStatus, setClientStatus] = useState<
    'active' | 'inactive' | 'draft'
  >('draft');
  const [displayClientId, setDisplayClientId] = useState<string | null>(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');

  // ── Form state ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<AddClientDetails>(() => {
    // Check if there's a persisted draft for this client
    if (clientId) {
      const persisted = getDraft(clientId);
      if (persisted) {
        const restored = { ...initialFormData, ...persisted };
        // NOTE: DO NOT set committedFormData here
        // committedFormData must always represent the last committed API state
        // for correct change detection in processStep1 (PATCH on Dalej).
        // It will be set after the API fetch completes.
        // Store the original profile path for potential deletion
        if (persisted.profileResource?.path) {
          originalProfilePath.current = persisted.profileResource.path;
        }
        return restored;
      }
    }
    return initialFormData;
  });
  const [errors, setErrors] = useState<ClientErrors>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [showList, setShowList] = useState(true);

  // ── Profile ───────────────────────────────────────────────────────────────
  // profileApiUrl  — URL for display: either from draft (user's working state)
  //                  or from API (server state). NEVER from deleted state (null).
  // profileBase64  — base64 preview after the user picks a new file locally
  // profileDeletedByUser — explicit flag when user clicks delete button
  const [profileApiUrl, setProfileApiUrl] = useState('');
  const [profileBase64, setProfileBase64] = useState(() =>
    clientId ? (getDraft(clientId)?.profileBase64 ?? '') : ''
  );
  const [profileDeletedByUser, setProfileDeletedByUser] = useState(false);

  // ── Contract docs ─────────────────────────────────────────────────────────
  const [persistedContractDocs, setPersistedContractDocs] = useState<
    MediaResource[]
  >(() => (clientId ? (getDraft(clientId)?.contractDocs ?? []) : []));
  const [contractUploadProgress, setContractUploadProgress] = useState<
    Record<string, number>
  >({});
  const [contractFailedFiles, setContractFailedFiles] = useState<string[]>([]);
  const [contractDocResources, setContractDocResources] = useState<
    Record<string, MediaResource>
  >({});
  // Paths of existing docs the user removed — deletion deferred to save so
  // cancelling doesn't permanently destroy server-side files.
  const [deletedDocPaths, setDeletedDocPaths] = useState<string[]>([]);

  // ── Subscription plans ────────────────────────────────────────────────────
  const [currentPlanId, setCurrentPlanId] = useState('');
  const [currentPlanLabel, setCurrentPlanLabel] = useState('');
  const [availablePlans, setAvailablePlans] = useState<
    { id: string; label: string }[]
  >([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [plansPage, setPlansPage] = useState(1);
  const [hasMorePlans, setHasMorePlans] = useState(true);
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const debouncedPlanSearch = useDebounce(planSearchQuery, 500);
  const [searchResults, setSearchResults] = useState<
    { id: string; label: string }[]
  >([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [selectedPlanLabel, setSelectedPlanLabel] = useState('');

  // ── Fetch existing client data ────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) {
      return;
    }
    const fetchData = async () => {
      setIsLoadingClientData(true);
      try {
        const result = await viewClient(clientId);
        if (result.success && result.data) {
          const client = result.data.client;
          const populated: AddClientDetails = {
            name: client.name ?? '',
            email: client.email ?? '',
            phone: client.contactInfo?.phoneNumber ?? '',
            countryCode: client.contactInfo?.countryCode ?? '+48',
            currency: client.currency ?? 'PLN',
            address1: client.address?.addressLine1 ?? '',
            address2: client.address?.addressLine2 ?? '',
            city: client.address?.city ?? '',
            state: client.address?.state ?? '',
            zip: client.address?.zipCode ?? '',
            country: client.address?.country ?? 'Polska',
            nip: client.complianceDetails?.nip ?? '',
            subscriptionPlan:
              client.subscriptionDetails?.subscriptionPlanId ?? '',
            paymentStatus: client.subscriptionDetails?.paymentStatus ?? '',
            billing_company_name: client.billingDetails?.companyName ?? '',
            billing_nip: formatNip(client.billingDetails?.nip),
            billing_regon: client.billingDetails?.regon ?? '',
            billing_krs: client.billingDetails?.krs ?? '',
            invoice_address_line_1:
              client.billingDetails?.invoiceAddress?.addressLine1 ?? '',
            invoice_address_line_2:
              client.billingDetails?.invoiceAddress?.addressLine2 ?? '',
            invoice_zip_code:
              client.billingDetails?.invoiceAddress?.zipCode ?? '',
            invoice_city: client.billingDetails?.invoiceAddress?.city ?? '',
            invoice_province:
              client.billingDetails?.invoiceAddress?.province ?? '',
          };

          // Always store the original profile path from API
          // This is the baseline for detecting changes/deletions
          if (client.profileUrl?.path) {
            originalProfilePath.current = client.profileUrl.path;
          } else {
            originalProfilePath.current = null;
          }

          // CRITICAL: committedFormData must ALWAYS be set to original API data
          // for correct change detection in Step 2. This baseline never changes
          // until a successful PATCH is made.
          committedFormData.current = populated;

          // Only update form display if there's no persisted draft
          // If draft exists, use it as complete source of truth — don't merge/override
          const persistedDraft = getDraft(clientId);
          if (!persistedDraft) {
            setFormData(populated);
          }
          // If draft exists: formData already loaded from draft at initialization
          // Don't override any fields from API response — draft is source of truth

          setClientStatus(client.status ?? 'draft');
          setDisplayClientId(client.clientId ?? null);

          // Set profileApiUrl: priority is deletion intent > draft upload > API
          // Never restore API profile if user already deleted it
          if (persistedDraft?.profileDeletionIntent) {
            // Profile was deleted by user — do not restore from API
            setProfileApiUrl('');
          } else if (persistedDraft?.profileResource?.path) {
            // Draft has working profile (user uploaded it in step 1)
            setProfileApiUrl(persistedDraft.profileResource.path);
          } else if (client.profileUrl?.url) {
            // No draft profile, load from API
            setProfileApiUrl(client.profileUrl.url);
          } else {
            // No profile anywhere
            setProfileApiUrl('');
          }

          const docs: MediaResource[] = (
            client.complianceDetails?.docs ?? []
          ).map((doc) => {
            // Backend returns a presigned URL — extract the S3 key from it
            // when the explicit path field is absent (pre-deployment fallback).
            let path = doc.path ?? '';
            if (!path && doc.url) {
              try {
                path = new URL(doc.url).pathname.slice(1);
              } catch {
                path = '';
              }
            }
            return {
              path,
              resourceId: doc.resourceId ?? '',
              isUploaded: doc.isUploaded ?? true,
              filename: doc.filename,
              fileType: doc.fileType ?? '',
              fileSize: doc.fileSize ?? 0,
            };
          });

          // Base: API docs (source of truth for confirmed server state).
          // Extend: docs saved in draft that are NOT yet in the API response —
          // these are newly uploaded files from processStep1 that haven't been
          // PATCH'd yet (e.g. user uploaded in step 1, refreshed on step 2).
          const apiDocPaths = new Set(docs.map((d) => d.path));
          const draftExtraDocs = (persistedDraft?.contractDocs ?? []).filter(
            (d) => d.path && !apiDocPaths.has(d.path)
          );
          setPersistedContractDocs([...docs, ...draftExtraDocs].slice(0, 2));

          if (client.subscriptionDetails?.subscriptionPlanId) {
            setCurrentPlanId(client.subscriptionDetails.subscriptionPlanId);
            setCurrentPlanLabel(client.subscriptionDetails.planName ?? '');
            setSelectedPlanLabel(client.subscriptionDetails.planName ?? '');
          }
        } else {
          setLoadError(result.error ?? CLIENT_ERROR_MESSAGES.DEFAULT_ERROR);
        }
      } catch {
        setLoadError(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      } finally {
        setIsLoadingClientData(false);
      }
    };
    void fetchData();
  }, [clientId, getDraft]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      setContractDocResources({});
    };
  }, []);

  // ── Fetch active subscription plans ───────────────────────────────────────
  useEffect(() => {
    const fetchFirstPage = async () => {
      setIsLoadingPlans(true);
      try {
        const result = await listSubscriptionApiService.listSubscriptions({
          status: 'active',
          page: 1,
          limit: 10,
        });
        setAvailablePlans(
          result.data.map((plan: SubscriptionPlan) => ({
            id: plan.id,
            label: plan.planName,
          }))
        );
        setHasMorePlans(result.hasNextPage);
        setPlansPage(1);
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchFirstPage();
  }, []);

  const fetchMorePlans = useCallback(async () => {
    if (isLoadingPlans || !hasMorePlans) {
      return;
    }
    setIsLoadingPlans(true);
    const nextPage = plansPage + 1;
    try {
      const result = await listSubscriptionApiService.listSubscriptions({
        status: 'active',
        page: nextPage,
        limit: 10,
      });
      setAvailablePlans((prev) => {
        const newItems = result.data
          .map((plan: SubscriptionPlan) => ({
            id: plan.id,
            label: plan.planName,
          }))
          .filter((newItem) => !prev.some((item) => item.id === newItem.id));
        return [...prev, ...newItems];
      });
      setHasMorePlans(result.hasNextPage);
      setPlansPage(result.page);
    } catch (err) {
      console.error('Error fetching more subscription plans:', err);
    } finally {
      setIsLoadingPlans(false);
    }
  }, [isLoadingPlans, hasMorePlans, plansPage]);

  // Plan search
  useEffect(() => {
    if (!debouncedPlanSearch) {
      setSearchResults([]);
      return;
    }
    const fetchSearchResults = async () => {
      setIsSearchLoading(true);
      try {
        const result = await listSubscriptionApiService.listSubscriptions({
          status: 'active',
          search: debouncedPlanSearch,
          page: 1,
          limit: 10,
        });
        setSearchResults(
          result.data.map((plan: SubscriptionPlan) => ({
            id: plan.id,
            label: plan.planName,
          }))
        );
      } catch (err) {
        console.error('Error searching subscription plans:', err);
      } finally {
        setIsSearchLoading(false);
      }
    };
    fetchSearchResults();
  }, [debouncedPlanSearch]);

  const handlePlanSearchChange = useCallback((query: string) => {
    setPlanSearchQuery(query);
    // Show loader immediately on every keystroke so the dropdown never flashes
    // "no results found" during the debounce window before the fetch starts.
    setIsSearchLoading(query.length > 0);
  }, []);

  // Current plan shown as disabled at the top of the dropdown; excluded from
  // the selectable active-plans list to avoid a duplicate entry.
  const plansForDropdown = useMemo(() => {
    const base = planSearchQuery ? searchResults : availablePlans;
    const withoutCurrent = currentPlanId
      ? base.filter((p) => p.id !== currentPlanId)
      : base;
    if (!currentPlanId) {
      return withoutCurrent;
    }
    return [
      {
        id: currentPlanId,
        label: currentPlanLabel,
        disabled: true,
        disabledTooltip: 'To jest aktualny plan',
        disabledTooltipSide: 'right' as const,
      },
      ...withoutCurrent,
    ];
  }, [
    availablePlans,
    searchResults,
    planSearchQuery,
    currentPlanId,
    currentPlanLabel,
  ]);

  // ── Field handlers ────────────────────────────────────────────────────────
  const makeFieldHandler = useCallback(
    (field: keyof ClientErrors, trim = false) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = trim ? e.target.value.trim() : e.target.value.trimStart();
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: '' }));
        }
      },
    [errors]
  );

  const handleNameChange = makeFieldHandler('name');

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim().slice(0, 9);
      setFormData((prev) => ({ ...prev, phone: value }));
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
    },
    [errors.phone]
  );

  const handleAddress1Change = makeFieldHandler('address1');

  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        address2: e.target.value.trimStart(),
      }));
    },
    []
  );

  const handleCityChange = makeFieldHandler('city', true);
  const handleStateChange = makeFieldHandler('state');
  const handleZipChange = makeFieldHandler('zip', true);

  const handleNipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const formatted = formatNip(e.target.value);
      setFormData((prev) => ({ ...prev, nip: formatted }));
      if (errors.nip) {
        setErrors((prev) => ({ ...prev, nip: '' }));
      }
    },
    [errors.nip]
  );

  // Invoice details handlers
  const handleBillingCompanyNameChange = makeFieldHandler(
    'billing_company_name'
  );
  const handleBillingNipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const formatted = formatNip(e.target.value);
      setFormData((prev) => ({ ...prev, billing_nip: formatted }));
      if (errors.billing_nip) {
        setErrors((prev) => ({ ...prev, billing_nip: '' }));
      }
    },
    [errors.billing_nip]
  );
  const handleBillingRegonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
      setFormData((prev) => ({ ...prev, billing_regon: digits }));
      if (errors.billing_regon) {
        setErrors((prev) => ({ ...prev, billing_regon: '' }));
      }
    },
    [errors.billing_regon]
  );
  const handleBillingKrsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, billing_krs: digits }));
      if (errors.billing_krs) {
        setErrors((prev) => ({ ...prev, billing_krs: '' }));
      }
    },
    [errors.billing_krs]
  );
  const handleInvoiceAddressLine1Change = makeFieldHandler(
    'invoice_address_line_1'
  );
  const handleInvoiceAddressLine2Change = makeFieldHandler(
    'invoice_address_line_2'
  );
  const handleInvoiceZipCodeChange = makeFieldHandler('invoice_zip_code', true);
  const handleInvoiceCityChange = makeFieldHandler('invoice_city');
  const handleInvoiceProvinceChange = makeFieldHandler('invoice_province');

  const handleCurrencySelect = useCallback((id: string) => {
    setFormData((prev) => ({ ...prev, currency: id }));
    setErrors((prev) => ({ ...prev, currency: '' }));
  }, []);

  const handleSubscriptionPlanSelect = useCallback(
    (id: string) => {
      const base = planSearchQuery ? searchResults : availablePlans;
      const label = base.find((p) => p.id === id)?.label ?? '';
      setSelectedPlanLabel(label);
      setFormData((prev) => ({ ...prev, subscriptionPlan: id }));
      setErrors((prev) => ({ ...prev, subscriptionPlan: '' }));
    },
    [planSearchQuery, searchResults, availablePlans]
  );

  const handlePaymentStatusSelect = useCallback((id: string) => {
    setFormData((prev) => ({ ...prev, paymentStatus: id }));
    setErrors((prev) => ({ ...prev, paymentStatus: '' }));
  }, []);

  const handleProfileFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setFormData((prev) => ({ ...prev, profileFile: file }));
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const b64 = (ev.target?.result as string) ?? '';
          setProfileBase64(b64);
        };
        reader.readAsDataURL(file);
      } else {
        setProfileBase64('');
      }
    },
    []
  );

  const handleProfileFileDelete = useCallback(() => {
    setFormData((prev) => ({ ...prev, profileFile: undefined }));
    setProfileBase64('');
    setProfileApiUrl('');
    setProfileDeletedByUser(true);
  }, []);

  const handleProfileFileError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  // ── Contract file handlers ────────────────────────────────────────────────
  const handleContractFilesError = useCallback((message: string) => {
    setErrors((prev) => ({ ...prev, contractFiles: message }));
    setTimeout(() => {
      setErrors((prev) => ({ ...prev, contractFiles: '' }));
    }, 3000);
  }, []);

  const uploadContractFile = useCallback(
    async (file: File): Promise<MediaResource | null> => {
      const key = file.name;
      setContractUploadProgress((prev) => ({ ...prev, [key]: 0 }));
      setContractFailedFiles((prev) => prev.filter((f) => f !== key));

      try {
        const presignedResponse = await getPresignedUrlsBatch({
          paths: [
            `clients/${clientId}/docs/resourceid-placeholder/${file.name}`,
          ],
          generatePath: true,
        });

        const fileData = presignedResponse.files[0];
        if (!fileData) {
          throw new Error(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
        }

        await uploadFileToS3(fileData.url, file, (bytesLoaded) => {
          setContractUploadProgress((prev) => ({
            ...prev,
            [key]: bytesLoaded,
          }));
        });

        const resource: MediaResource = {
          path: fileData.path,
          resourceId: fileData.resourceId,
          isUploaded: true,
          filename: file.name,
          fileType: file.type,
          fileSize: file.size,
        };

        setContractDocResources((prev) => ({ ...prev, [key]: resource }));
        // Snap to 100% so the filled bar renders before removal.
        setContractUploadProgress((prev) => ({ ...prev, [key]: file.size }));
        await new Promise<void>((resolve) => setTimeout(resolve, 400));
        setContractUploadProgress((prev) => {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        });

        return resource;
      } catch {
        setContractUploadProgress((prev) => {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        });
        setContractFailedFiles((prev) => [
          ...prev.filter((f) => f !== key),
          key,
        ]);
        return null;
      }
    },
    [clientId]
  );

  const retryContractUpload = useCallback(
    (filename: string): void => {
      const file = formData.contractFiles?.find((f) => f.name === filename);
      if (file) {
        void uploadContractFile(file);
      }
    },
    [formData.contractFiles, uploadContractFile]
  );

  const handleContractFilesSelect = useCallback((files: File[]) => {
    setFormData((prev) => ({ ...prev, contractFiles: files }));
    if (files.length > 0) {
      setErrors((prev) => ({ ...prev, contractFiles: '' }));
    }

    // Clean up resources for files that were removed
    setContractDocResources((prev) => {
      const kept: Record<string, MediaResource> = {};
      for (const f of files) {
        const existing = prev[f.name];
        if (existing) {
          kept[f.name] = existing;
        }
      }
      return kept;
    });
    setContractFailedFiles((prev) =>
      prev.filter((name) => files.some((f) => f.name === name))
    );

    // Simulate progress bar for newly added files — no real upload yet.
    // Actual upload happens in processStep1 when user clicks "Dalej".
    for (const file of files) {
      const key = file.name;
      setContractUploadProgress((prev) => {
        if (key in prev) {
          return prev;
        } // already animating
        return { ...prev, [key]: 0 };
      });

      let loaded = 0;
      const step = file.size / 10;
      const timer = setInterval(() => {
        loaded = Math.min(loaded + step, file.size);
        setContractUploadProgress((prev) => ({ ...prev, [key]: loaded }));
        if (loaded >= file.size) {
          clearInterval(timer);
          setTimeout(() => {
            setContractUploadProgress((prev) => {
              const { [key]: _done, ...rest } = prev;
              return rest;
            });
          }, 400);
        }
      }, 100);
    }
  }, []);

  const handleExistingContractDelete = useCallback((path: string) => {
    if (!path) {
      return;
    }
    // Remove from UI immediately; queue server-side deletion for save time.
    // This matches the technician pattern — no API call until user clicks save.
    setPersistedContractDocs((prev) => prev.filter((doc) => doc.path !== path));
    setDeletedDocPaths((prev) => [...prev, path]);
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep1 = useCallback((): boolean => {
    const hasContracts =
      (formData.contractFiles && formData.contractFiles.length > 0) ||
      persistedContractDocs.length > 0;

    const validationErrors: ClientErrors = {
      ...initialErrors,
      name: validateName(formData.name),
      phone: validatePhone(formData.phone),
      address1: validateAddress(formData.address1),
      city: validateCity(formData.city),
      state: validateState(formData.state),
      zip: validateZip(formData.zip),
      nip: validateNip(formData.nip),
      billing_company_name: validateBillingCompanyName(
        formData.billing_company_name
      ),
      billing_nip: validateBillingNip(formData.billing_nip),
      billing_regon: validateBillingRegon(formData.billing_regon),
      billing_krs: validateBillingKrs(formData.billing_krs),
      invoice_address_line_1: validateInvoiceAddressLine1(
        formData.invoice_address_line_1
      ),
      invoice_zip_code: validateInvoiceZipCode(formData.invoice_zip_code),
      invoice_city: validateInvoiceCity(formData.invoice_city),
      subscriptionPlan: !formData.subscriptionPlan
        ? CLIENT_ERROR_MESSAGES.REQUIRED
        : '',
      paymentStatus: !formData.paymentStatus
        ? CLIENT_ERROR_MESSAGES.REQUIRED
        : '',
      contractFiles: !hasContracts ? CLIENT_ERROR_MESSAGES.REQUIRED : '',
    };
    setErrors(validationErrors);
    return !Object.values(validationErrors).some((e) => e !== '');
  }, [formData, persistedContractDocs]);

  // ── Step 1 — Dalej (validate + upload + PATCH + save draft + navigate) ─────
  // Same pattern as Add Client PATCH mode: commit all step-1 changes to the
  // server on every "Dalej" click so state is always consistent on refresh.
  const processStep1 = useCallback(async () => {
    if (!validateStep1()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const committed = committedFormData.current ?? initialFormData;
      const profileChanged = formData.profileFile !== committed.profileFile;
      const profileAdded = profileChanged && Boolean(formData.profileFile);
      const profileDeleted =
        profileDeletedByUser ||
        (profileChanged &&
          !formData.profileFile &&
          Boolean(originalProfilePath.current));

      // 1. Upload new profile to S3
      let newProfileResource: MediaResource | undefined;
      if (profileAdded && formData.profileFile) {
        const presignedResponse = await getPresignedUrlsBatch({
          paths: [
            `clients/${clientId}/profiles/resourceid-placeholder/${formData.profileFile.name}`,
          ],
          generatePath: true,
        });
        const fileData = presignedResponse.files[0];
        if (fileData) {
          await uploadFileToS3(fileData.url, formData.profileFile);
          newProfileResource = {
            path: fileData.path,
            resourceId: fileData.resourceId,
            isUploaded: true,
            filename: formData.profileFile.name,
            fileType: formData.profileFile.type,
            fileSize: formData.profileFile.size,
          };
        }
      }

      // 2. Delete old profile from S3 immediately (same as Add Client pattern)
      if (profileDeleted && originalProfilePath.current) {
        try {
          await deleteMediaResource(originalProfilePath.current);
        } catch {
          setSnackbarError('Failed to delete profile. Please try again.');
          setIsSnackbarOpen(true);
          return;
        }
      }

      // 3. Delete queued doc paths (deferred from handleExistingContractDelete)
      if (deletedDocPaths.length > 0) {
        await Promise.all(
          deletedDocPaths.map((path) => deleteMediaResource(path))
        );
        setDeletedDocPaths([]);
      }

      // 4. Upload new contract files silently — simulated progress was already
      // shown on file selection. Files retried via retryContractUpload are
      // cached in contractDocResources so we skip re-uploading them.
      const newDocs: MediaResource[] = [];
      const contractFiles = formData.contractFiles ?? [];
      if (contractFiles.length > 0) {
        const toUpload = contractFiles.filter(
          (f) => !contractDocResources[f.name]
        );

        // Collect already-retried files from cache
        for (const file of contractFiles) {
          const cached = contractDocResources[file.name];
          if (cached) {
            newDocs.push(cached);
          }
        }

        if (toUpload.length > 0) {
          const presignedResponse = await getPresignedUrlsBatch({
            paths: toUpload.map(
              (f) => `clients/${clientId}/docs/resourceid-placeholder/${f.name}`
            ),
            generatePath: true,
          });

          const failedNames: string[] = [];
          for (let i = 0; i < toUpload.length; i++) {
            const file = toUpload[i];
            const fileData = presignedResponse.files[i];
            if (!file) {
              continue;
            }
            try {
              if (!fileData) {
                throw new Error();
              }
              await uploadFileToS3(fileData.url, file);
              newDocs.push({
                path: fileData.path,
                resourceId: fileData.resourceId,
                isUploaded: true,
                filename: file.name,
                fileType: file.type,
                fileSize: file.size,
              });
            } catch {
              failedNames.push(file.name);
            }
          }

          if (failedNames.length > 0) {
            setContractFailedFiles(failedNames);
            setSnackbarError(
              'Some documents failed to upload. Please retry or remove them.'
            );
            setIsSnackbarOpen(true);
            return;
          }
        }
      }

      // 5. Build PATCH payload — only send what changed
      const textDiff = buildTextDiff(formData, committed);
      const patchPayload: ClientPatchPayload = {
        ...textDiff,
        ...(newProfileResource ? { profile: newProfileResource } : {}),
        ...(profileDeleted ? { profile: null } : {}),
        ...(newDocs.length > 0 ? { docs: newDocs } : {}),
      };

      // 6. PATCH the server (skip if nothing changed)
      if (Object.keys(patchPayload).length > 0) {
        const result = await editActiveClient(clientId, patchPayload);
        if (!result.success) {
          setSnackbarError(result.error || CLIENT_ERROR_MESSAGES.DEFAULT_ERROR);
          setIsSnackbarOpen(true);
          return;
        }
      }

      // 7. Commit local state to match what was just saved
      committedFormData.current = { ...formData };
      if (newProfileResource) {
        originalProfilePath.current = newProfileResource.path;
      } else if (profileDeleted) {
        originalProfilePath.current = null;
      }
      setCurrentPlanId(formData.subscriptionPlan);
      setCurrentPlanLabel(selectedPlanLabel);

      const committedDocs = [...persistedContractDocs, ...newDocs].slice(0, 2);
      setPersistedContractDocs(committedDocs);
      setContractDocResources({});
      // Clear the File objects — they've been committed to persistedContractDocs.
      // Without this, returning to step 1 shows the same file twice (once as a
      // persisted doc, once as a still-selected File in formData.contractFiles).
      setFormData((prev) => ({ ...prev, contractFiles: undefined }));
      setProfileDeletedByUser(false);

      // 8. Save draft with the committed (clean) state — no pending intents
      saveDraft(clientId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: formData.countryCode,
        currency: formData.currency,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        nip: formData.nip,
        subscriptionPlan: formData.subscriptionPlan,
        subscriptionPlanLabel: selectedPlanLabel,
        paymentStatus: formData.paymentStatus,
        contractDocs: committedDocs,
        billing_company_name: formData.billing_company_name,
        billing_nip: formData.billing_nip,
        billing_regon: formData.billing_regon,
        billing_krs: formData.billing_krs,
        invoice_address_line_1: formData.invoice_address_line_1,
        invoice_address_line_2: formData.invoice_address_line_2,
        invoice_zip_code: formData.invoice_zip_code,
        invoice_city: formData.invoice_city,
        invoice_province: formData.invoice_province,
        // profileResource saved for originalProfilePath restoration on refresh
        ...(newProfileResource ? { profileResource: newProfileResource } : {}),
        // profileBase64 saved for preview display on refresh (before API loads)
        ...(profileBase64 ? { profileBase64 } : {}),
      });

      navigate(`/Klienci/Edycja-klienta/${clientId}/krok-2`);
    } catch {
      setSnackbarError(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setIsSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateStep1,
    formData,
    clientId,
    deletedDocPaths,
    contractDocResources,
    persistedContractDocs,
    navigate,
    saveDraft,
    selectedPlanLabel,
    profileBase64,
    profileDeletedByUser,
  ]);

  // ── Step 2 — final save ───────────────────────────────────────────────────
  // All PATCH work (text diff, profile upload/delete, docs) was already
  // committed in processStep1 on every "Dalej" click. Step 2 just clears
  // the draft and navigates back to the list.
  const handleStep2Save = useCallback(async () => {
    setIsSaving(true);
    try {
      if (clientId) {
        clearDraft(clientId);
      }
      navigate('/Klienci', {
        replace: true,
        state: { showEditClientSuccess: true },
      });
    } catch {
      setSnackbarError(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setIsSnackbarOpen(true);
      setIsSaving(false);
    }
  }, [clientId, clearDraft, navigate]);

  // ── Cancel / navigation ───────────────────────────────────────────────────
  // Pending edits exist only against the last committed snapshot. Step-1 edits
  // are already PATCH-committed by handleForwardToStep2 before step 2 mounts,
  // so the same check works uniformly for both steps.
  const hasPendingChanges = useMemo(() => {
    const baseline = committedFormData.current;
    if (!baseline) {
      return false;
    }
    if (Object.keys(buildTextDiff(formData, baseline)).length > 0) {
      return true;
    }
    if (formData.profileFile) {
      return true;
    }
    if (profileDeletedByUser) {
      return true;
    }
    if (formData.contractFiles && formData.contractFiles.length > 0) {
      return true;
    }
    if (deletedDocPaths.length > 0) {
      return true;
    }
    return false;
  }, [formData, profileDeletedByUser, deletedDocPaths]);

  const handleCancel = useCallback(() => {
    if (hasPendingChanges) {
      setIsCancelModalOpen(true);
      return;
    }
    if (clientId) {
      clearDraft(clientId);
    }
    navigate('/Klienci');
  }, [hasPendingChanges, clientId, clearDraft, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    // Clear upload caches before navigating away
    setContractDocResources({});
    // Clear draft to prevent stale state on next edit
    if (clientId) {
      clearDraft(clientId);
    }
    navigate('/Klienci');
  }, [navigate, clientId, clearDraft]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  const handleBackToStep1 = useCallback(() => {
    navigate(`/Klienci/Edycja-klienta/${clientId}/krok-1`);
  }, [navigate, clientId]);

  const handleForwardToStep2 = useCallback(async () => {
    await processStep1();
  }, [processStep1]);

  const isPlanSearchActive = Boolean(planSearchQuery);

  // ── Client status handlers ─────────────────────────────────────────────────

  const handleStatusChange = useCallback(() => {
    setIsStatusDialogOpen(true);
  }, []);

  const handleCloseStatusDialog = useCallback(() => {
    if (!isStatusSubmitting) {
      setIsStatusDialogOpen(false);
    }
  }, [isStatusSubmitting]);

  const handleConfirmStatusUpdate = useCallback(async () => {
    if (!clientId) {
      return;
    }
    const isCurrentlyActive = clientStatus === 'active';
    setIsStatusSubmitting(true);
    const result = await updateClientStatusApiService.updateClientStatus({
      clientId,
      isActive: !isCurrentlyActive,
    });
    setIsStatusSubmitting(false);
    setIsStatusDialogOpen(false);
    if (result.success) {
      const newStatus: 'active' | 'inactive' = isCurrentlyActive
        ? 'inactive'
        : 'active';
      setClientStatus(newStatus);
      setStatusSuccessMessage(
        isCurrentlyActive
          ? CLIENT_LABELS.STATUS_SUCCESS_DEACTIVATED
          : CLIENT_LABELS.STATUS_SUCCESS_ACTIVATED
      );
      setShowStatusSuccessSnackbar(true);
    } else {
      setStatusErrorMessage(result.error ?? CLIENT_LABELS.STATUS_ERROR_GENERIC);
      setShowStatusErrorSnackbar(true);
    }
  }, [clientId, clientStatus]);

  const handleCloseStatusSuccessSnackbar = useCallback(() => {
    setShowStatusSuccessSnackbar(false);
    setStatusSuccessMessage('');
  }, []);

  const handleCloseStatusErrorSnackbar = useCallback(() => {
    setShowStatusErrorSnackbar(false);
    setStatusErrorMessage('');
  }, []);

  // ── Step 1 completeness (cursor hint for Step 2 tab) ─────────────────────
  const isEditClientEnabled = useMemo(() => {
    const hasContracts =
      (formData.contractFiles && formData.contractFiles.length > 0) ||
      persistedContractDocs.length > 0;
    return (
      !validateName(formData.name) &&
      !validatePhone(formData.phone) &&
      !validateAddress(formData.address1) &&
      !validateCity(formData.city) &&
      !validateState(formData.state) &&
      !validateNip(formData.nip) &&
      Boolean(formData.subscriptionPlan) &&
      Boolean(formData.paymentStatus) &&
      hasContracts
    );
  }, [formData, persistedContractDocs]);

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    step,
    clientId,
    formData,
    errors,
    isSubmitting,
    isSaving,
    isLoadingClientData,
    loadError,
    snackbarError,
    isSnackbarOpen,
    isCancelModalOpen,
    showList,
    setShowList,
    handleNameChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleNipChange,
    handleBillingCompanyNameChange,
    handleBillingNipChange,
    handleBillingRegonChange,
    handleBillingKrsChange,
    handleInvoiceAddressLine1Change,
    handleInvoiceAddressLine2Change,
    handleInvoiceZipCodeChange,
    handleInvoiceCityChange,
    handleInvoiceProvinceChange,
    handleCurrencySelect,
    handleSubscriptionPlanSelect,
    selectedPlanLabel,
    handlePaymentStatusSelect,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleContractFilesError,
    handleContractFilesSelect,
    contractUploadProgress,
    contractFailedFiles,
    retryContractUpload,
    handleExistingContractDelete,
    handleStep2Save,
    handleForwardToStep2,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    handleBackToStep1,
    // Dropdown receives: current plan (disabled) prepended to active plans
    availablePlans: plansForDropdown,
    isLoadingPlans: planSearchQuery ? isSearchLoading : isLoadingPlans,
    hasMorePlans: planSearchQuery ? false : hasMorePlans,
    fetchMorePlans,
    handlePlanSearchChange,
    isPlanSearchActive,
    planSearchQuery,
    isSearchLoading,
    persistedContractDocs,
    // Show base64 preview if user picked a new file, else the API URL
    profileDisplayUrl: profileBase64 || profileApiUrl,
    currentPlanId,
    clientStatus,
    displayClientId,
    isStatusDialogOpen,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleStatusChange,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    handleCloseStatusSuccessSnackbar,
    handleCloseStatusErrorSnackbar,
    isEditClientEnabled,
  };
};
